import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import Button from '@mui/material/Button';
import { useAppSelector } from '../../../../@types/redux';
import { PillButton, ControlledTextField, ControlledMuiAutoCompleteSelect } from '@zz2/zz2-ui';
import { ControlledLabelledCheckBox } from '../../../../components/input/form/ControlledLabelledCheckBoxWrapper';
import { IOptionType } from '../../../../@types/helper';
import PrintServerModelHelper from '../../../../@types/model/masterData/printServer/printServerModelHelper';
import { yupResolver } from '@hookform/resolvers/yup';
import { IPrintServerFormValues } from '../../../../@types/model/masterData/printServer/printServerFormValues';
import CustomReactHookFormDevTools from '../../../../components/devtools/ReactHookFormDevTools';

interface IPrintServerFormProps {
    isLoading : boolean;
    initialValues : IPrintServerFormValues;
    onSubmit : (values : IPrintServerFormValues) => void;
    onClose : () => void;
}

const PrintServerForm = (props : IPrintServerFormProps) : React.ReactElement => {
    const auth = useAppSelector(state => state.auth);
    const sites = useAppSelector(state => state.masterData.sites);

    const { handleSubmit, control, formState: { isValid, isDirty }, trigger } = useForm<IPrintServerFormValues>({
        defaultValues: props.initialValues,
        resolver: yupResolver(PrintServerModelHelper.formSchema()),
        mode: 'onChange',
    });

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        trigger();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const siteOptions : Array<IOptionType> = useMemo(() => {
        return sites?.filter(x => x.isActive && auth.session?.user.siteIds.some(y => y === x.id))
            .map(x => ({ label: `(${x.code})  ${x.description}`, value: x.id })) ?? [];
    }, [sites, auth.session?.user.siteIds]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <div>
            <CustomReactHookFormDevTools control={control} />
            <form onSubmit={handleSubmit(props.onSubmit)}>
                <div className={'fdc p20'}>
                    <ControlledTextField
                        className={'mb15'}
                        name={'name'}
                        label={'Name'}
                        control={control}
                        defaultValue={props.initialValues.name ?? ''}
                        size={'small'}
                    />
                    <ControlledTextField
                        className={'mb15'}
                        name={'macAddress'}
                        label={'MAC Address'}
                        control={control}
                        defaultValue={props.initialValues.macAddress ?? ''}
                        size={'small'}
                    />
                    <ControlledTextField
                        className={'mb15'}
                        name={'ipAddress'}
                        label={'IP Address'}
                        control={control}
                        defaultValue={props.initialValues.ipAddress ?? ''}
                        size={'small'}
                    />
                    <ControlledTextField
                        className={'mb15'}
                        name={'primaryUrl'}
                        label={'Primary URL'}
                        control={control}
                        defaultValue={props.initialValues.primaryUrl ?? ''}
                        size={'small'}
                    />
                    <ControlledTextField
                        className={'mb15'}
                        name={'defaultScheme'}
                        label={'Default Scheme'}
                        control={control}
                        defaultValue={props.initialValues.defaultScheme ?? ''}
                        size={'small'}
                    />
                    <ControlledTextField
                        className={'mb10'}
                        name={'port'}
                        label={'Port'}
                        control={control}
                        type={'number'}
                        defaultValue={props.initialValues.port?.toString() ?? ''}
                        size={'small'}
                    />
                    <ControlledMuiAutoCompleteSelect
                        className={'mb10'}
                        name={'site'}
                        label={'Site'}
                        control={control}
                        options={siteOptions}
                        defaultValues={props.initialValues.site ?? null}
                    />
                    <div className={'fdr jcfs mb5'}>
                        <div className={'mr10'}>
                            <ControlledLabelledCheckBox
                                name={'isMafrodaIntegrated'}
                                label={'Is Mafroda Integrated'}
                                control={control}
                                defaultValue={props.initialValues.isMafrodaIntegrated}
                            />
                        </div>
                        <div className={'mr10'}>
                            <ControlledLabelledCheckBox
                                name={'isOverrideFlag'}
                                label={'Override Flag'}
                                control={control}
                                defaultValue={props.initialValues.isOverrideFlag}
                            />
                        </div>
                        <div className={'flx1 fdr jcfs'}>
                            <ControlledLabelledCheckBox
                                name={'isActive'}
                                label={'Is Active'}
                                control={control}
                                defaultValue={props.initialValues.isActive}
                                disabled={props.initialValues.isActive}
                            />
                        </div>
                    </div>
                    <div className={'fdr flx1 aic jcfe'}>
                        <Button
                            className={'cpd mr20'}
                            type={'button'}
                            onClick={props.onClose}
                        >
                            CANCEL
                        </Button>
                        <PillButton
                            disabled={!isDirty || !isValid || props.isLoading}
                            className={'ml15 pl20 pr20 h35'}
                            text={'Save'}
                            type={'submit'}
                            color={'secondary'}
                        />
                    </div>
                </div>
            </form>
        </div>
    );
};

export default PrintServerForm;
